import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://fricotrade.com.br";
const OG_IMAGE = `${SITE_URL}/icons/logo-frico.png`;

type Meta = { title: string; description: string };

const DEFAULT_META: Meta = {
  title: "Fricó Trade — Gestão de Trade Marketing e Equipe de Campo",
  description:
    "Plataforma Fricó Alimentos para planejar visitas, acompanhar promotores em campo, auditar PDVs e medir indicadores de trade marketing em tempo real.",
};

const ROUTE_META: Record<string, Meta> = {
  "/": {
    title: "Painel de Trade Marketing | Fricó Trade",
    description:
      "Visão geral da operação de campo: visitas do dia, cobertura de PDVs, desempenho dos promotores e alertas em um único painel.",
  },
  "/login": {
    title: "Entrar na plataforma | Fricó Trade",
    description:
      "Acesse a plataforma Fricó Trade para gerenciar visitas, promotores, auditorias de PDV e indicadores de trade marketing.",
  },
  "/reset-password": {
    title: "Redefinir senha | Fricó Trade",
    description:
      "Redefina a senha da sua conta Fricó Trade com segurança e volte a acompanhar a operação de campo.",
  },
  "/equipe": {
    title: "Equipe de Campo | Fricó Trade",
    description:
      "Cadastre e organize promotores e supervisores, defina regiões de atuação e acompanhe a produtividade da equipe de campo.",
  },
  "/pdvs": {
    title: "Pontos de Venda (PDVs) | Fricó Trade",
    description:
      "Gerencie o cadastro de pontos de venda, endereços, canais e coordenadas para roteirizar visitas com precisão.",
  },
  "/regioes": {
    title: "Regiões e Rotas | Fricó Trade",
    description:
      "Defina regiões comerciais, agrupe PDVs e monte rotas otimizadas de visita para cada promotor de campo.",
  },
  "/tarefas": {
    title: "Tarefas de Campo | Fricó Trade",
    description:
      "Padronize as tarefas executadas em cada visita: reposição, precificação, montagem de ponto extra e checagens obrigatórias.",
  },
  "/campanhas": {
    title: "Campanhas de Trade | Fricó Trade",
    description:
      "Crie campanhas promocionais com SKUs e materiais de PDV e acompanhe a execução em cada ponto de venda.",
  },
  "/planejamento": {
    title: "Planejamento de Visitas | Fricó Trade",
    description:
      "Monte a agenda diária e semanal da equipe, distribua PDVs por promotor e programe visitas recorrentes.",
  },
  "/execucao": {
    title: "Execução em Campo | Fricó Trade",
    description:
      "Check-in e check-out com GPS, registro de fotos antes e depois e conclusão de tarefas direto do ponto de venda.",
  },
  "/monitoramento": {
    title: "Monitoramento em Tempo Real | Fricó Trade",
    description:
      "Acompanhe no mapa o status das visitas do dia, atrasos, check-ins em aberto e a posição da equipe em campo.",
  },
  "/auditoria": {
    title: "Auditoria de PDV | Fricó Trade",
    description:
      "Aplique checklists de auditoria, classifique cada PDV como OK, Atenção ou Crítico e acompanhe planos de ação.",
  },
  "/indicadores": {
    title: "Indicadores e KPIs | Fricó Trade",
    description:
      "Acompanhe cobertura, produtividade, tempo em loja e demais KPIs da operação de trade marketing por período.",
  },
  "/relatorios": {
    title: "Relatórios Gerenciais | Fricó Trade",
    description:
      "Gere e exporte relatórios de visitas, auditorias, preços e desempenho da equipe para análise gerencial.",
  },
  "/configuracoes": {
    title: "Configurações da Conta | Fricó Trade",
    description:
      "Ajuste preferências da conta, notificações, segurança e parâmetros gerais da operação na plataforma Fricó Trade.",
  },
  "/evidencias": {
    title: "Evidências Fotográficas | Fricó Trade",
    description:
      "Galeria unificada de fotos de visitas, auditorias e preços, com aprovação e curadoria das evidências de campo.",
  },
  "/desempenho": {
    title: "Desempenho dos Promotores | Fricó Trade",
    description:
      "Ranking e notas de A a E dos promotores, com histórico semanal de pontuação e evolução da execução em campo.",
  },
  "/admin/usuarios": {
    title: "Administração de Usuários | Fricó Trade",
    description:
      "Área administrativa para criar usuários, atribuir papéis e controlar o acesso às funcionalidades da plataforma.",
  },
  "/precos": {
    title: "Inteligência de Preços | Fricó Trade",
    description:
      "Registre preços praticados no PDV, compare com a concorrência e acompanhe a evolução por produto e região.",
  },
  "/chat": {
    title: "Chat Interno da Equipe | Fricó Trade",
    description:
      "Converse com promotores e supervisores em tempo real, com envio de fotos e histórico por conversa.",
  },
  "/pesquisas": {
    title: "Pesquisas de Campo | Fricó Trade",
    description:
      "Crie formulários dinâmicos vinculados às visitas e colete respostas estruturadas direto do ponto de venda.",
  },
  "/loja-perfeita": {
    title: "Loja Perfeita | Fricó Trade",
    description:
      "Defina critérios de loja perfeita, meça a conformidade de cada PDV e acompanhe o score de execução.",
  },
  "/validade": {
    title: "Controle de Validade | Fricó Trade",
    description:
      "Monitore datas de validade dos produtos no PDV e receba alertas antecipados para evitar perdas.",
  },
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  const meta = ROUTE_META[pathname] ?? DEFAULT_META;
  const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
};

export default RouteSeo;
